export interface OverdueTransaction {
  id: string;
  client_id: string;
  client_name: string;
  client_document?: string; // CPF/CNPJ
  client_email?: string;
  client_phone?: string;
  description: string;
  original_amount: number;
  current_amount: number; // Com juros/multas
  due_date: Date;
  days_overdue: number;
  category_id?: string;
  category_name?: string;
  payment_account_id?: string;
  payment_account_name?: string;
  created_at: Date;
  last_contact?: Date;
  last_contact_type?: 'email' | 'phone' | 'whatsapp' | 'letter';
  status: OverdueStatus;
  priority: OverduePriority;
  notes?: string;
  installment_number?: number;
  total_installments?: number;
  contract_reference?: string;
}

export interface OverdueClient {
  id: string;
  name: string;
  document?: string;
  email?: string;
  phone?: string;
  total_debt: number;
  oldest_debt_date: Date;
  total_transactions: number;
  last_contact?: Date;
  status: ClientStatus;
  risk_level: RiskLevel;
  payment_history_score: number; // 0-100
}

export interface OverdueGroup {
  client: OverdueClient;
  transactions: OverdueTransaction[];
  total_amount: number;
  oldest_transaction: OverdueTransaction;
  newest_transaction: OverdueTransaction;
  average_days_overdue: number;
}

export interface OverdueFilters {
  status: OverdueStatus[];
  priority: OverduePriority[];
  days_overdue_min?: number;
  days_overdue_max?: number;
  amount_min?: number;
  amount_max?: number;
  client_search?: string;
  due_date_from?: Date;
  due_date_to?: Date;
  categories?: string[];
  payment_accounts?: string[];
  last_contact_days?: number; // Últimos X dias de contato
  sort_by: OverdueSortField;
  sort_direction: 'asc' | 'desc';
}

export interface OverdueSummary {
  total_overdue_amount: number;
  total_transactions: number;
  total_clients: number;
  average_days_overdue: number;
  by_status: Record<OverdueStatus, {
    count: number;
    amount: number;
  }>;
  by_priority: Record<OverduePriority, {
    count: number;
    amount: number;
  }>;
  by_age_range: {
    '1-30': { count: number; amount: number };
    '31-60': { count: number; amount: number };
    '61-90': { count: number; amount: number };
    '90+': { count: number; amount: number };
  };
}

export interface OverdueAction {
  id: string;
  transaction_ids: string[];
  action_type: OverdueActionType;
  description: string;
  amount?: number;
  due_date?: Date;
  notes?: string;
  created_by: string;
  created_at: Date;
  status: 'pending' | 'completed' | 'failed';
}

export interface ContactAttempt {
  id: string;
  transaction_id: string;
  client_id: string;
  contact_type: 'email' | 'phone' | 'whatsapp' | 'letter' | 'visit';
  contact_method: string; // email address, phone number, etc.
  message?: string;
  status: 'sent' | 'delivered' | 'read' | 'failed' | 'bounced';
  attempted_at: Date;
  attempted_by: string;
  response_received?: boolean;
  response_date?: Date;
  response_notes?: string;
}

export interface PaymentPlan {
  id: string;
  transaction_ids: string[];
  client_id: string;
  original_amount: number;
  negotiated_amount: number;
  discount_amount: number;
  installments: PaymentPlanInstallment[];
  status: 'active' | 'completed' | 'cancelled' | 'defaulted';
  created_at: Date;
  approved_by: string;
  notes?: string;
}

export interface PaymentPlanInstallment {
  id: string;
  payment_plan_id: string;
  installment_number: number;
  due_date: Date;
  amount: number;
  paid_amount?: number;
  paid_date?: Date;
  status: 'pending' | 'paid' | 'overdue';
}

// Enums
export enum OverdueStatus {
  OVERDUE = 'atrasado',
  IN_PROGRESS = 'em_andamento',
  RESOLVED = 'resolvido',
  WRITTEN_OFF = 'baixado',
  LEGAL_ACTION = 'acao_judicial'
}

export enum OverduePriority {
  LOW = 'baixa',
  MEDIUM = 'media',
  HIGH = 'alta',
  CRITICAL = 'critica'
}

export enum ClientStatus {
  ACTIVE = 'ativo',
  NEGOTIATING = 'negociando',
  INACTIVE = 'inativo',
  BLOCKED = 'bloqueado'
}

export enum RiskLevel {
  LOW = 'baixo',
  MEDIUM = 'medio',
  HIGH = 'alto',
  VERY_HIGH = 'muito_alto'
}

export enum OverdueActionType {
  SEND_EMAIL = 'enviar_email',
  MAKE_CALL = 'fazer_ligacao',
  SEND_WHATSAPP = 'enviar_whatsapp',
  SEND_LETTER = 'enviar_carta',
  SCHEDULE_VISIT = 'agendar_visita',
  CREATE_PAYMENT_PLAN = 'criar_plano_pagamento',
  APPLY_DISCOUNT = 'aplicar_desconto',
  WRITE_OFF = 'dar_baixa',
  SEND_TO_LEGAL = 'enviar_juridico'
}

export type OverdueSortField = 
  | 'client_name' 
  | 'due_date' 
  | 'days_overdue' 
  | 'amount' 
  | 'last_contact' 
  | 'priority';

// Props para componentes
export interface OverdueTableProps {
  data: OverdueGroup[];
  onSelectTransaction: (transactionId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  selectedTransactions: Set<string>;
  onRowClick?: (group: OverdueGroup) => void;
  loading?: boolean;
  sortable?: boolean;
}

export interface OverdueFiltersProps {
  filters: OverdueFilters;
  onFiltersChange: (filters: OverdueFilters) => void;
  summary: OverdueSummary;
  availableCategories?: Array<{ id: string; name: string }>;
  availableAccounts?: Array<{ id: string; name: string }>;
}

export interface OverdueActionsProps {
  selectedTransactions: Set<string>;
  onActionComplete: () => void;
  disabled?: boolean;
}

// Constantes
export const OVERDUE_STATUS_LABELS = {
  [OverdueStatus.OVERDUE]: 'Atrasadas',
  [OverdueStatus.IN_PROGRESS]: 'Em andamento',
  [OverdueStatus.RESOLVED]: 'Resolvidas',
  [OverdueStatus.WRITTEN_OFF]: 'Baixadas',
  [OverdueStatus.LEGAL_ACTION]: 'Ação judicial'
} as const;

export const OVERDUE_PRIORITY_LABELS = {
  [OverduePriority.LOW]: 'Baixa',
  [OverduePriority.MEDIUM]: 'Média',
  [OverduePriority.HIGH]: 'Alta',
  [OverduePriority.CRITICAL]: 'Crítica'
} as const;

export const OVERDUE_STATUS_COLORS = {
  [OverdueStatus.OVERDUE]: '#EF4444', // red-500
  [OverdueStatus.IN_PROGRESS]: '#F59E0B', // amber-500
  [OverdueStatus.RESOLVED]: '#10B981', // green-500
  [OverdueStatus.WRITTEN_OFF]: '#6B7280', // gray-500
  [OverdueStatus.LEGAL_ACTION]: '#7C3AED' // violet-600
} as const;

export const OVERDUE_PRIORITY_COLORS = {
  [OverduePriority.LOW]: '#10B981', // green-500
  [OverduePriority.MEDIUM]: '#F59E0B', // amber-500
  [OverduePriority.HIGH]: '#EF4444', // red-500
  [OverduePriority.CRITICAL]: '#DC2626' // red-600
} as const;

export const RISK_LEVEL_COLORS = {
  [RiskLevel.LOW]: '#10B981', // green-500
  [RiskLevel.MEDIUM]: '#F59E0B', // amber-500
  [RiskLevel.HIGH]: '#EF4444', // red-500
  [RiskLevel.VERY_HIGH]: '#DC2626' // red-600
} as const;

// Utilitários
export const calculatePriority = (daysOverdue: number, amount: number): OverduePriority => {
  if (daysOverdue >= 90 || amount >= 50000) return OverduePriority.CRITICAL;
  if (daysOverdue >= 60 || amount >= 20000) return OverduePriority.HIGH;
  if (daysOverdue >= 30 || amount >= 5000) return OverduePriority.MEDIUM;
  return OverduePriority.LOW;
};

export const calculateRiskLevel = (
  paymentHistoryScore: number, 
  daysOverdue: number, 
  totalDebt: number
): RiskLevel => {
  const riskScore = 
    (100 - paymentHistoryScore) * 0.4 + 
    Math.min(daysOverdue / 90, 1) * 100 * 0.4 + 
    Math.min(totalDebt / 100000, 1) * 100 * 0.2;

  if (riskScore >= 80) return RiskLevel.VERY_HIGH;
  if (riskScore >= 60) return RiskLevel.HIGH;
  if (riskScore >= 40) return RiskLevel.MEDIUM;
  return RiskLevel.LOW;
};

export const formatOverdueDays = (days: number): string => {
  if (days === 1) return '1 dia';
  if (days < 30) return `${days} dias`;
  if (days < 60) return `${Math.floor(days / 30)} mês`;
  if (days < 365) return `${Math.floor(days / 30)} meses`;
  return `${Math.floor(days / 365)} ano(s)`;
};

export const getOverdueAgeRange = (days: number): '1-30' | '31-60' | '61-90' | '90+' => {
  if (days <= 30) return '1-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
};

export interface OverdueAccount {
  id: string;
  description: string;
  amount: number;
  due_date: Date;
  days_overdue: number;
  category_id?: number;
  category_name?: string;
  payment_account_id?: number;
  payment_account_name?: string;
  client?: string;
  supplier?: string;
  is_recurring?: boolean;
  total_installments?: number;
  current_installment?: number;
}